/**
 * Reusable skeleton loader — respects `prefers-reduced-motion` via CSS
 * (`.fl-skeleton`). Use in place of ad-hoc "Loading…" text so the layout
 * stays stable while data streams in.
 *
 * Compose with `SkeletonBlock` for common shapes:
 *   <SkeletonBlock lines={3} />                 // 3 stacked text lines
 *   <SkeletonBlock variant="stat" />            // stat-card scaffold
 *   <SkeletonBlock variant="row" count={5} />   // 5 list rows
 */
import React from "react";

export function Skeleton({ className = "", style = {}, "data-testid": testid }) {
  return (
    <div
      className={`fl-skeleton ${className}`}
      style={style}
      data-testid={testid || "skeleton"}
      aria-hidden="true"
    />
  );
}

const variants = {
  stat: (i) => (
    <div className="fl-card p-6" key={i}>
      <Skeleton className="h-5 w-5 rounded-md" />
      <Skeleton className="h-3 w-24 mt-3" />
      <Skeleton className="h-10 w-32 mt-2 rounded-lg" />
      <Skeleton className="h-1.5 w-full mt-3 rounded-full" />
    </div>
  ),
  row: (i) => (
    <div className="flex items-center gap-3 py-3" key={i}>
      <Skeleton className="h-10 w-10 rounded-full" />
      <div className="flex-1">
        <Skeleton className="h-3 w-40" />
        <Skeleton className="h-2.5 w-64 mt-2" />
      </div>
      <Skeleton className="h-8 w-16 rounded-md" />
    </div>
  ),
  card: (i) => (
    <div className="fl-card p-5" key={i}>
      <Skeleton className="h-4 w-32" />
      <Skeleton className="h-3 w-full mt-3" />
      <Skeleton className="h-3 w-5/6 mt-2" />
      <Skeleton className="h-3 w-4/6 mt-2" />
    </div>
  ),
  lines: (i, extra) => (
    <div className="space-y-2" key={i}>
      {Array.from({ length: extra?.lines || 3 }).map((_, k) => (
        <Skeleton key={k} className={`h-3 ${k === 0 ? "w-5/6" : k === 1 ? "w-full" : "w-4/6"}`} />
      ))}
    </div>
  ),
};

export function SkeletonBlock({ variant = "lines", count = 1, lines, className = "" }) {
  const render = variants[variant] || variants.lines;
  return (
    <div className={className}>
      {Array.from({ length: count }).map((_, i) => render(i, { lines }))}
    </div>
  );
}

export default Skeleton;
